import { useEffect, useRef, useState, FormEvent } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { LogoUpload } from '../components/LogoUpload';
import { SignaturePad, type SignaturePadHandle } from '../components/SignaturePad';
import { useUserOrg } from '../lib/org';
import { TRADES, RISKY_ACTIVITIES, type OrgRoleValue } from '../lib/features';
import { useSession } from '../lib/auth';
import {
  listSites,
  listOrgDocuments,
  createSite,
  updateOrgProfile,
  createDocument,
  type Site,
  type DocumentRow,
} from '../lib/db';
import { uploadDocument } from '../lib/storage';
import { lookupPostcode, isValidPostcodeFormat } from '../lib/postcode';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { DOC_TEMPLATES } from '../data/docTemplates';
import { generateDocumentPdf } from '../lib/pdf';

const TOTAL_STEPS = 6;
const WELCOME_DONE_KEY = 'hs_welcome_done';

async function blobToDataUrl(blob: Blob): Promise<string | null> {
  return new Promise<string | null>((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });
}

export function Welcome() {
  const nav = useNavigate();
  const { org, refresh: refreshOrg } = useUserOrg();
  const { session } = useSession();
  useDocumentTitle('Welcome');

  // Synchronous check — avoids a one-frame flash of step 1 when the user hits
  // Back and React Router lands them on /welcome after they've already
  // completed the wizard. Using lazy init so this runs before the first render.
  const [alreadyDone] = useState<boolean>(() => {
    try { return localStorage.getItem(WELCOME_DONE_KEY) === '1'; } catch { return false; }
  });

  const [step, setStep] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Step 3 — H&S Policy
  const policySigRef = useRef<SignaturePadHandle>(null);
  const [directorName, setDirectorName] = useState('');

  // Step 5 — first site (was step 4 before profile picker was added)
  const [siteName, setSiteName] = useState('');
  const [postcode, setPostcode] = useState('');
  const [pcStatus, setPcStatus] = useState<'idle' | 'checking' | 'ok' | 'fail'>('idle');
  const [pcCoords, setPcCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [projectType, setProjectType] = useState<'quick_job' | 'small_refurb' | 'full_site' | null>(null);
  const [inCharge, setInCharge] = useState<boolean | null>(null);

  // Step 4 — profile picker (role / trades / risky activities)
  const [orgRole, setOrgRole] = useState<OrgRoleValue | null>(null);
  const [tradeKeys, setTradeKeys] = useState<string[]>([]);
  const [activityKeys, setActivityKeys] = useState<string[]>([]);

  const [policyExists, setPolicyExists] = useState(false);
  const [siteExists, setSiteExists] = useState(false);

  // On mount, see what's already done so we can skip past steps.
  useEffect(() => {
    if (!org) return;
    const userName = (session?.user.user_metadata?.full_name as string | undefined) ?? '';
    if (userName && !directorName) setDirectorName(userName);
    Promise.all([listSites(org.org_id), listOrgDocuments(org.org_id)])
      .then(([sites, docs]: [Site[], DocumentRow[]]) => {
        if (sites.length > 0) setSiteExists(true);
        if (docs.some((d) => d.template_id === 'hs_policy_statement' && d.is_current)) {
          setPolicyExists(true);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org]);

  function finish() {
    try { localStorage.setItem(WELCOME_DONE_KEY, '1'); } catch { /* noop */ }
    nav('/');
  }

  function next() { setStep((n) => Math.min(n + 1, TOTAL_STEPS)); setError(null); }
  function back() { setStep((n) => Math.max(n - 1, 1)); setError(null); }

  // Reaching the celebration step counts as completing the wizard.
  // Without this, clicking one of the deep links on step 5 (Training register,
  // Plant register, etc.) and then closing that page bounces the user back
  // through Dashboard's auto-redirect → fresh wizard from step 1.
  useEffect(() => {
    if (step === 6) {
      try { localStorage.setItem(WELCOME_DONE_KEY, '1'); } catch { /* noop */ }
    }
  }, [step]);

  // Postcode lookup for step 4
  async function checkPostcode() {
    if (!isValidPostcodeFormat(postcode)) { setPcStatus('fail'); return; }
    setPcStatus('checking');
    try {
      const r = await lookupPostcode(postcode);
      if (r) { setPcStatus('ok'); setPcCoords({ lat: r.latitude, lng: r.longitude }); }
      else { setPcStatus('fail'); setPcCoords(null); }
    } catch {
      setPcStatus('fail');
    }
  }

  async function savePolicy(e: FormEvent) {
    e.preventDefault();
    if (!org) { setError('Loading… please try again.'); return; }
    if (!directorName.trim()) { setError('Director name required.'); return; }
    if (policySigRef.current?.isEmpty()) { setError('Please sign the policy.'); return; }
    setBusy(true);
    setError(null);
    try {
      const tpl = DOC_TEMPLATES.find((t) => t.id === 'hs_policy_statement');
      if (!tpl) throw new Error('H&S Policy template not found.');

      const docId = crypto.randomUUID();
      const dateStr = new Date().toLocaleDateString('en-GB', {
        day: '2-digit', month: 'long', year: 'numeric',
      });
      let signatureDataUrl: string | null = null;
      if (policySigRef.current && !policySigRef.current.isEmpty()) {
        signatureDataUrl = await blobToDataUrl(await policySigRef.current.toBlob());
      }

      const fields = tpl.fields.map((f) => {
        if (f.key === 'company_name') return { key: f.key, label: f.label, value: org.org_name };
        if (f.key === 'director_name') return { key: f.key, label: f.label, value: directorName.trim() };
        if (f.key === 'review_date') {
          const d = new Date();
          d.setFullYear(d.getFullYear() + 1);
          return { key: f.key, label: f.label, value: d.toISOString().slice(0, 10) };
        }
        return { key: f.key, label: f.label, value: '' };
      });

      const blob = await generateDocumentPdf({
        orgName: org.org_name,
        orgLogoPath: org.logo_path,
        title: tpl.title,
        category: tpl.category,
        scope: tpl.scope,
        siteName: null,
        fields,
        sections: tpl.sections.map((sec) => ({ heading: sec.heading, body: sec.body })),
        signatureDataUrl,
        signatoryName: directorName.trim(),
        date: dateStr,
        notes: null,
      });

      const fileName = `hs_policy_v1_${dateStr.replace(/\s+/g, '-')}.pdf`;
      const file = new File([blob], fileName, { type: 'application/pdf' });
      const path = await uploadDocument(org.org_id, docId, file);

      await createDocument({
        id: docId,
        orgId: org.org_id,
        siteId: null,
        templateId: tpl.id,
        title: tpl.title,
        category: tpl.category,
        scope: tpl.scope,
        status: 'issued',
        filePath: path,
        fileName,
        mimeType: 'application/pdf',
        sizeBytes: file.size,
      });
      setPolicyExists(true);
      next();
    } catch (err) {
      console.error('[Welcome.savePolicy]', err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function saveProfile() {
    if (!org) { setError('Loading… please try again.'); return; }
    if (!orgRole) { setError('Pick which fits best — you can change it later in Settings.'); return; }
    setBusy(true);
    setError(null);
    try {
      await updateOrgProfile(org.org_id, {
        role: orgRole,
        trades: tradeKeys,
        riskyActivities: activityKeys,
        profileCompleted: true,
      });
      await refreshOrg();
      next();
    } catch (err) {
      console.error('[Welcome.saveProfile]', err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function saveFirstSite(e: FormEvent) {
    e.preventDefault();
    if (!org) { setError('Loading… please try again.'); return; }
    if (!siteName.trim()) { setError('Site name required.'); return; }
    setBusy(true);
    setError(null);
    try {
      await createSite({
        orgId: org.org_id,
        name: siteName.trim(),
        address: postcode.trim() || null,
        centreLat: pcCoords?.lat ?? null,
        centreLng: pcCoords?.lng ?? null,
        geofenceRadiusM: 250,
        projectType: projectType,
        inCharge: orgRole === 'runs_jobs' ? true
              : orgRole === 'works_for_others' ? false
              : inCharge,
      });
      setSiteExists(true);
      next();
    } catch (err) {
      console.error('[Welcome.saveFirstSite]', err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  // Early-return BEFORE rendering the wizard so there's no flash of step 1.
  if (alreadyDone) {
    return <Navigate to="/" replace />;
  }

  // ----- Step counter normalisation -----
  // Internally we have 6 stops: 1 = intro, 2 = logo, 3 = policy, 4 = profile,
  // 5 = first site, 6 = done. Only the middle four are real work, so the
  // progress bar counts those as "Step N of 4" and the intro / done screens
  // hide it entirely. Otherwise the user lands and sees "Step 1 of 6" while
  // the intro itself says "Four short steps" — exactly the contradiction the
  // audit flagged.
  const SETUP_STEPS = 4;
  const isSetupStep = step >= 2 && step <= 5;
  const setupStepNumber = step - 1;        // step 2 → 1, step 5 → 4

  return (
    <div className="auth-shell" style={{ alignItems: 'flex-start', paddingTop: 32 }}>
      <div className="auth-card welcome-card">
        {isSetupStep && (
          <div className="welcome-progress">
            <div className="welcome-progress-bar">
              <div className="welcome-progress-fill" style={{ width: `${(setupStepNumber / SETUP_STEPS) * 100}%` }} />
            </div>
            <span className="welcome-progress-label">Step {setupStepNumber} of {SETUP_STEPS}</span>
          </div>
        )}

        {error && <div className="banner banner-error">{error}</div>}

        {step === 1 && (
          <div>
            <h1 style={{ marginTop: 0 }}>Welcome to {org?.org_name ?? 'your H&amp;S app'}</h1>
            <p>Let's get your account set up. Four short steps, about 5 minutes:</p>
            <ol style={{ paddingLeft: 18, lineHeight: 1.7 }}>
              <li>Upload your company logo (it goes on every PDF)</li>
              <li>Sign your top-level Health &amp; Safety Policy</li>
              <li>Tell us about your work (so we show the right templates)</li>
              <li>Add your first site</li>
            </ol>
            <p className="muted" style={{ fontSize: 13 }}>
              You can skip any step and come back later from Settings.
            </p>
            <div className="btn-stack">
              <button type="button" className="btn btn-primary" onClick={next}>Let's go →</button>
              <button type="button" className="btn btn-ghost" onClick={finish}>Skip wizard — go to Dashboard</button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <h2 style={{ marginTop: 0 }}>Upload your logo</h2>
            <p className="muted" style={{ marginTop: 0 }}>
              Goes on every PDF — sign-ins, RAMS (Risk Assessment & Method Statements), accidents, permits. Square works best (256×256+).
            </p>
            {org && (
              <LogoUpload orgId={org.org_id} currentLogoPath={org.logo_path} onChange={refreshOrg} />
            )}
            <div className="btn-stack">
              <button type="button" className="btn btn-primary" onClick={next}>
                {org?.logo_path ? 'Looks good →' : 'Skip for now →'}
              </button>
              <button type="button" className="btn btn-ghost" onClick={back}>Back</button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div>
            <h2 style={{ marginTop: 0 }}>Sign your H&amp;S Policy Statement</h2>
            <p className="muted" style={{ marginTop: 0 }}>
              The law (Health & Safety at Work Act) says every business with 5 or more people
              needs a written H&S policy. We've pre-written it for you using your company name.
              You sign — done.
            </p>
            {policyExists ? (
              <div className="card" style={{ marginBottom: 12, background: 'var(--brand-pale)' }}>
                <p style={{ margin: 0 }}>
                  <strong>✓ Already done.</strong> Your H&amp;S Policy Statement is already issued.
                </p>
              </div>
            ) : (
              <form onSubmit={savePolicy}>
                <div className="field needs-input">
                  <label htmlFor="dir">Director (you, or whoever signs the policy) *</label>
                  <input
                    id="dir"
                    type="text"
                    value={directorName}
                    onChange={(e) => setDirectorName(e.target.value)}
                    required
                  />
                </div>
                <SignaturePad ref={policySigRef} label="Sign as Director" />
                <div className="btn-stack">
                  <button type="submit" className="btn btn-primary" disabled={busy}>
                    {busy ? 'Generating…' : 'Sign and continue →'}
                  </button>
                  <button type="button" className="btn btn-ghost" onClick={next} disabled={busy}>
                    Skip for now
                  </button>
                  <button type="button" className="btn btn-ghost" onClick={back} disabled={busy}>
                    Back
                  </button>
                </div>
              </form>
            )}
            {policyExists && (
              <div className="btn-stack">
                <button type="button" className="btn btn-primary" onClick={next}>Continue →</button>
                <button type="button" className="btn btn-ghost" onClick={back}>Back</button>
              </div>
            )}
          </div>
        )}

        {step === 4 && (
          <div>
            <h2 style={{ marginTop: 0 }}>Tell us about your work</h2>
            <p className="muted" style={{ marginTop: 0 }}>
              Three quick questions so we only show you the bits you actually need.
              You can change any of this later in Settings.
            </p>

            {/* ----- Q1: Role on site ----- */}
            <h3 className="section-title" style={{ marginBottom: 6 }}>Which fits you best?</h3>
            <div className="role-cards">
              <button type="button"
                className={`role-card${orgRole === 'works_for_others' ? ' selected' : ''}`}
                onClick={() => setOrgRole('works_for_others')}>
                <strong>I work on someone else's job</strong>
                <span>You're a trade going onto a site someone else runs. Your RAMS feed up to them.</span>
              </button>
              <button type="button"
                className={`role-card${orgRole === 'runs_jobs' ? ' selected' : ''}`}
                onClick={() => setOrgRole('runs_jobs')}>
                <strong>I run the job</strong>
                <span>You're the one in charge of the site — workers, subbies, the lot.</span>
              </button>
              <button type="button"
                className={`role-card${orgRole === 'both' ? ' selected' : ''}`}
                onClick={() => setOrgRole('both')}>
                <strong>Bit of both</strong>
                <span>Sometimes you run jobs, sometimes you work for someone else. We'll ask per-site.</span>
              </button>
            </div>

            {/* ----- Q2: Trades (multi-select) ----- */}
            <h3 className="section-title" style={{ marginTop: 24, marginBottom: 6 }}>What trade(s) do you do?</h3>
            <p className="muted" style={{ margin: '0 0 8px', fontSize: 13 }}>Tick everything that applies — we'll line up the right RAMS templates for you.</p>
            <div className="multi-select">
              {TRADES.map((t) => (
                <label key={t.key} className={`multi-select-item${tradeKeys.includes(t.key) ? ' selected' : ''}`}>
                  <input type="checkbox"
                    checked={tradeKeys.includes(t.key)}
                    onChange={(e) => {
                      setTradeKeys((prev) => e.target.checked ? [...prev, t.key] : prev.filter((k) => k !== t.key));
                    }} />
                  <span>{t.label}</span>
                </label>
              ))}
            </div>

            {/* ----- Q3: Risky activities ----- */}
            <h3 className="section-title" style={{ marginTop: 24, marginBottom: 6 }}>Do any of these apply?</h3>
            <p className="muted" style={{ margin: '0 0 8px', fontSize: 13 }}>Tick what you do regularly. We'll add the relevant RAMS, permits and training prompts.</p>
            <div className="multi-select">
              {RISKY_ACTIVITIES.map((a) => (
                <label key={a.key} className={`multi-select-item${activityKeys.includes(a.key) ? ' selected' : ''}`}>
                  <input type="checkbox"
                    checked={activityKeys.includes(a.key)}
                    onChange={(e) => {
                      setActivityKeys((prev) => e.target.checked ? [...prev, a.key] : prev.filter((k) => k !== a.key));
                    }} />
                  <span>{a.label}</span>
                </label>
              ))}
            </div>

            <div className="btn-stack" style={{ marginTop: 20 }}>
              <button type="button" className="btn btn-primary" disabled={busy || !orgRole} onClick={saveProfile}>
                {busy ? 'Saving…' : 'Continue →'}
              </button>
              <button type="button" className="btn btn-ghost" onClick={back} disabled={busy}>Back</button>
            </div>
          </div>
        )}

        {step === 5 && (
          <div>
            <h2 style={{ marginTop: 0 }}>Add your first site</h2>
            <p className="muted" style={{ marginTop: 0 }}>
              We'll use the postcode to drop a geofence so daily sign-ins know who's actually on site.
              Don't worry about getting it perfect — you can edit later.
            </p>
            {siteExists ? (
              <div className="card" style={{ marginBottom: 12, background: 'var(--brand-pale)' }}>
                <p style={{ margin: 0 }}>
                  <strong>✓ Already done.</strong> You've got at least one site set up.
                </p>
              </div>
            ) : (
              <form onSubmit={saveFirstSite}>
                <div className="field needs-input">
                  <label htmlFor="sname">Site name *</label>
                  <input
                    id="sname"
                    type="text"
                    placeholder="e.g. Bigby Lane Plot 1-4"
                    value={siteName}
                    onChange={(e) => setSiteName(e.target.value)}
                    required
                  />
                </div>
                <div className="field">
                  <label>What kind of job is this?</label>
                  <p className="muted" style={{ margin: '0 0 8px', fontSize: 13 }}>Tells us what daily checks you'll need.</p>
                  <div className="role-cards">
                    <button type="button"
                      className={`role-card${projectType === 'quick_job' ? ' selected' : ''}`}
                      onClick={() => setProjectType('quick_job')}>
                      <strong>Quick job</strong>
                      <span>1-2 days. In and out. No scaffold or excavation.</span>
                    </button>
                    <button type="button"
                      className={`role-card${projectType === 'small_refurb' ? ' selected' : ''}`}
                      onClick={() => setProjectType('small_refurb')}>
                      <strong>Small job</strong>
                      <span>A few days to a few weeks. No scaffold or major excavation.</span>
                    </button>
                    <button type="button"
                      className={`role-card${projectType === 'full_site' ? ' selected' : ''}`}
                      onClick={() => setProjectType('full_site')}>
                      <strong>Full site</strong>
                      <span>Extension, new build, multi-trade. Scaffold, excavation, the lot.</span>
                    </button>
                  </div>
                </div>

                {orgRole === 'both' && (
                  <div className="field">
                    <label>Are you in charge of this site?</label>
                    <div className="role-cards">
                      <button type="button"
                        className={`role-card${inCharge === true ? ' selected' : ''}`}
                        onClick={() => setInCharge(true)}>
                        <strong>Yes — I'm running it</strong>
                      </button>
                      <button type="button"
                        className={`role-card${inCharge === false ? ' selected' : ''}`}
                        onClick={() => setInCharge(false)}>
                        <strong>No — I'm working for someone else</strong>
                      </button>
                    </div>
                  </div>
                )}

                <div className="field">
                  <label htmlFor="pcode">Site postcode</label>
                  <div className="postcode-row">
                    <input
                      id="pcode"
                      type="text"
                      placeholder="e.g. GU2 7XX"
                      value={postcode}
                      onChange={(e) => { setPostcode(e.target.value); setPcStatus('idle'); }}
                    />
                    <button type="button" className="btn btn-ghost" onClick={checkPostcode}>
                      {pcStatus === 'checking' ? 'Checking…' : 'Check'}
                    </button>
                  </div>
                  {pcStatus === 'ok' && <p className="muted" style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--success, #2F6B47)' }}>📍 Got it — geofence will drop here.</p>}
                  {pcStatus === 'fail' && <p className="muted" style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--danger)' }}>Couldn't find that postcode. You can save without and add later.</p>}
                </div>
                <div className="btn-stack">
                  <button type="submit" className="btn btn-primary" disabled={busy}>
                    {busy ? 'Saving…' : 'Save site and continue →'}
                  </button>
                  <button type="button" className="btn btn-ghost" onClick={next} disabled={busy}>
                    Skip for now
                  </button>
                  <button type="button" className="btn btn-ghost" onClick={back} disabled={busy}>
                    Back
                  </button>
                </div>
              </form>
            )}
            {siteExists && (
              <div className="btn-stack">
                <button type="button" className="btn btn-primary" onClick={next}>Continue →</button>
                <button type="button" className="btn btn-ghost" onClick={back}>Back</button>
              </div>
            )}
          </div>
        )}

        {step === 6 && (
          <div>
            <h2 style={{ marginTop: 0 }}>You're set up. 🎉</h2>
            <p>
              Logo on file, H&amp;S Policy signed, first site ready. Now let's
              put the app to work and make your first RAMS — the actual job
              you came here to skip a Sunday night on.
            </p>
            <div className="card" style={{ background: 'var(--brand-pale)', borderColor: 'var(--brand-light)', marginBottom: 14 }}>
              <p style={{ margin: 0, fontWeight: 500 }}>Next: your first RAMS</p>
              <p className="muted" style={{ margin: '6px 0 0', fontSize: 13 }}>
                We've picked a template for your trade. Tick what applies on
                your site, hit Generate, and you'll have a branded PDF in
                your library inside two minutes. Then you can poke around
                the rest of the app.
              </p>
            </div>
            <div className="btn-stack">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  try { localStorage.setItem(WELCOME_DONE_KEY, '1'); } catch { /* noop */ }
                  nav('/welcome/first-rams');
                }}
              >
                Make my first RAMS →
              </button>
              <button type="button" className="btn btn-ghost" onClick={finish}>
                Skip — take me to the dashboard
              </button>
            </div>
            <p className="muted" style={{ fontSize: 12, marginTop: 18 }}>
              You can always come back and explore the <Link to="/registers">Registers</Link>{' '}
              (team, plant, chemicals, subbies) and the{' '}
              <Link to="/documents">Document library</Link> from the menu.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
